import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Package, Heart, User, Star, ChevronRight, Edit2, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

import { API_BASE as API } from "@/config/api";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-blue-100 text-blue-700",
  preparing: "bg-purple-100 text-purple-700",
  packed: "bg-indigo-100 text-indigo-700",
  out_for_delivery: "bg-orange-100 text-orange-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-gray-100 text-gray-700",
};

const STATUS_STEPS = ["pending", "confirmed", "preparing", "packed", "out_for_delivery", "delivered"];

export default function CustomerDashboard() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profileEdit, setProfileEdit] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [profileSaving, setProfileSaving] = useState(false);

  useEffect(() => {
    if (tab === "orders") {
      setLoading(true);
      axios.get(`${API}/orders`, { withCredentials: true }).then(r => setOrders(r.data || [])).finally(() => setLoading(false));
    }
    if (tab === "wishlist") {
      setLoading(true);
      axios.get(`${API}/wishlist`, { withCredentials: true }).then(r => setWishlist(r.data || [])).finally(() => setLoading(false));
    }
  }, [tab]);

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm("Cancel this order?")) return;
    try {
      await axios.put(`${API}/orders/${orderId}/cancel`, {}, { withCredentials: true });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: "cancelled" } : o));
    } catch (e) { alert(e.response?.data?.detail || "Cannot cancel this order"); }
  };

  const handleProfileSave = async () => {
    setProfileSaving(true);
    try {
      await updateProfile(profileForm);
      setProfileEdit(false);
    } catch (e) { console.error(e); }
    finally { setProfileSaving(false); }
  };

  const handleRemoveWishlist = async (productId) => {
    await axios.delete(`${API}/wishlist/${productId}`, { withCredentials: true });
    setWishlist(prev => prev.filter(p => p.id !== productId));
  };

  const TABS = [
    { id: "orders", label: "My Orders", icon: Package },
    { id: "wishlist", label: "Wishlist", icon: Heart },
    { id: "profile", label: "Profile", icon: User },
  ];

  return (
    <div className="min-h-screen bg-petal-beige" data-testid="customer-dashboard">
      <div className="bg-[#F5F0E8] py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <p className="font-dancing text-petal-gold text-xl mb-1">Hello, {user?.name}!</p>
          <h1 className="font-playfair text-4xl sm:text-5xl text-petal-olive-text">My Dashboard</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <aside className="md:w-60 shrink-0">
            <div className="bg-white border border-petal-border overflow-hidden">
              <div className="bg-petal-olive-text p-6">
                <div className="w-14 h-14 rounded-full bg-petal-gold flex items-center justify-center mb-3">
                  <span className="font-playfair text-2xl text-white">{user?.name?.[0]?.toUpperCase()}</span>
                </div>
                <p className="font-playfair text-lg text-petal-beige">{user?.name}</p>
                <p className="font-outfit text-xs text-petal-border/70 truncate">{user?.email}</p>
              </div>
              <nav className="p-2">
                {TABS.map(t => {
                  const Icon = t.icon;
                  return (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                      className={`w-full flex items-center gap-3 px-4 py-3 font-outfit text-sm transition-colors ${tab === t.id ? "bg-petal-beige-alt text-petal-olive font-medium" : "text-petal-olive-text hover:bg-petal-beige-alt"}`}>
                      <Icon className="w-4 h-4" /> {t.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1">
            {/* Orders */}
            {tab === "orders" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-playfair text-2xl text-petal-olive-text mb-6">My Orders</h2>
                {loading ? (
                  <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="bg-white border border-petal-border h-24 animate-pulse" />)}</div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-16 bg-white border border-petal-border">
                    <Package className="w-12 h-12 text-petal-border mx-auto mb-4" />
                    <p className="font-playfair text-xl text-petal-muted mb-4">No orders yet</p>
                    <button onClick={() => navigate("/shop")} className="btn-primary text-xs py-2 px-6">Start Shopping</button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map(order => (
                      <div key={order.id} className="bg-white border border-petal-border p-6" data-testid={`order-${order.id}`}>
                        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-4">
                          <div>
                            <p className="font-outfit text-xs text-petal-muted uppercase tracking-widest">Order #{order.order_number}</p>
                            <p className="font-outfit text-xs text-petal-muted">{new Date(order.created_at).toLocaleDateString("en-IN", { year:"numeric", month:"long", day:"numeric" })}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 text-xs font-outfit uppercase tracking-wide font-medium rounded-sm ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-700"}`}>
                              {order.status?.replace(/_/g, " ")}
                            </span>
                            {["pending","confirmed"].includes(order.status) && (
                              <button onClick={() => handleCancelOrder(order.id)} data-testid={`cancel-order-${order.id}`}
                                className="text-red-400 font-outfit text-xs hover:text-red-500 underline">Cancel</button>
                            )}
                          </div>
                        </div>

                        {/* Status Progress */}
                        {order.status !== "cancelled" && order.status !== "refunded" && (
                          <div className="mb-4">
                            <div className="flex justify-between mb-1">
                              {STATUS_STEPS.map((s, i) => {
                                const currentIdx = STATUS_STEPS.indexOf(order.status);
                                const isComplete = i <= currentIdx;
                                return (
                                  <div key={s} className="flex flex-col items-center" style={{ width: `${100 / STATUS_STEPS.length}%` }}>
                                    <div className={`w-3 h-3 rounded-full ${isComplete ? "bg-petal-olive" : "bg-petal-border"}`} />
                                    <p className={`font-outfit text-xs mt-1 text-center hidden sm:block ${isComplete ? "text-petal-olive" : "text-petal-border"}`}>
                                      {s.replace(/_/g, " ")}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="relative h-1 bg-petal-border rounded-full mt-1">
                              <div className="absolute left-0 top-0 h-1 bg-petal-olive rounded-full transition-all"
                                style={{ width: `${(STATUS_STEPS.indexOf(order.status) / (STATUS_STEPS.length - 1)) * 100}%` }} />
                            </div>
                          </div>
                        )}

                        {/* Items */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {order.items?.slice(0, 3).map((item, i) => (
                            <div key={i} className="flex items-center gap-2 border border-petal-border p-2">
                              <img src={item.product_image || "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=100&q=80"} alt=""
                                className="w-10 h-10 object-cover" />
                              <div>
                                <p className="font-outfit text-xs text-petal-olive-text line-clamp-1">{item.product_name}</p>
                                <p className="font-outfit text-xs text-petal-muted">x{item.quantity} · ₹{item.price}</p>
                              </div>
                            </div>
                          ))}
                          {order.items?.length > 3 && (
                            <div className="border border-petal-border p-2 flex items-center justify-center w-14 h-14">
                              <span className="font-outfit text-xs text-petal-muted">+{order.items.length - 3}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-between items-center border-t border-petal-border pt-4">
                          <p className="font-outfit text-xs text-petal-muted">Payment: <span className="uppercase font-medium text-petal-olive-text">{order.payment_method}</span></p>
                          <p className="font-playfair text-lg text-petal-olive-text font-semibold">₹{order.total?.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Wishlist */}
            {tab === "wishlist" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-playfair text-2xl text-petal-olive-text mb-6">My Wishlist</h2>
                {loading ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{[...Array(3)].map((_, i) => <div key={i} className="bg-white border border-petal-border h-60 animate-pulse" />)}</div>
                ) : wishlist.length === 0 ? (
                  <div className="text-center py-16 bg-white border border-petal-border">
                    <Heart className="w-12 h-12 text-petal-border mx-auto mb-4" />
                    <p className="font-playfair text-xl text-petal-muted mb-4">Your wishlist is empty</p>
                    <button onClick={() => navigate("/shop")} className="btn-primary text-xs py-2 px-6">Browse Gifts</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {wishlist.map(product => (
                      <div key={product.id} className="bg-white border border-petal-border group">
                        <div className="relative aspect-square overflow-hidden cursor-pointer" onClick={() => navigate(`/shop/${product.id}`)}>
                          <img src={product.images?.[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        </div>
                        <div className="p-4">
                          <p className="font-playfair text-sm text-petal-olive-text line-clamp-1 mb-1">{product.name}</p>
                          <p className="font-outfit text-base font-semibold text-petal-olive-text mb-3">₹{product.price.toLocaleString()}</p>
                          <div className="flex gap-2">
                            <button onClick={() => navigate(`/shop/${product.id}`)} className="flex-1 btn-primary text-xs py-2">View</button>
                            <button onClick={() => handleRemoveWishlist(product.id)}
                              className="w-9 h-8 border border-red-200 text-red-400 hover:bg-red-50 transition-colors flex items-center justify-center">
                              <Heart className="w-4 h-4 fill-red-300" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Profile */}
            {tab === "profile" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-playfair text-2xl text-petal-olive-text">My Profile</h2>
                  {!profileEdit && (
                    <button onClick={() => setProfileEdit(true)} data-testid="edit-profile-btn"
                      className="flex items-center gap-2 font-outfit text-xs uppercase tracking-widest text-petal-olive hover:text-petal-olive-dark">
                      <Edit2 className="w-4 h-4" /> Edit
                    </button>
                  )}
                </div>
                <div className="bg-white border border-petal-border p-8 max-w-lg">
                  <div className="w-20 h-20 rounded-full bg-petal-gold flex items-center justify-center mb-6">
                    <span className="font-playfair text-3xl text-white">{user?.name?.[0]?.toUpperCase()}</span>
                  </div>
                  {profileEdit ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block font-outfit text-xs uppercase tracking-widest text-petal-olive-text mb-1.5">Full Name</label>
                        <input value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                          data-testid="profile-name-input"
                          className="w-full px-4 py-2.5 border border-petal-border bg-petal-beige font-outfit text-sm focus:outline-none focus:border-petal-olive" />
                      </div>
                      <div>
                        <label className="block font-outfit text-xs uppercase tracking-widest text-petal-olive-text mb-1.5">Phone</label>
                        <input value={profileForm.phone} onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                          data-testid="profile-phone-input"
                          className="w-full px-4 py-2.5 border border-petal-border bg-petal-beige font-outfit text-sm focus:outline-none focus:border-petal-olive" />
                      </div>
                      <div>
                        <label className="block font-outfit text-xs uppercase tracking-widest text-petal-muted mb-1.5">Email (cannot change)</label>
                        <input value={user?.email} disabled
                          className="w-full px-4 py-2.5 border border-petal-border bg-petal-beige-alt font-outfit text-sm text-petal-muted" />
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button onClick={handleProfileSave} disabled={profileSaving} data-testid="save-profile-btn"
                          className="btn-primary text-xs py-2.5 px-6 flex items-center gap-2 disabled:opacity-50">
                          <Check className="w-4 h-4" /> {profileSaving ? "Saving..." : "Save Changes"}
                        </button>
                        <button onClick={() => setProfileEdit(false)} className="btn-outline text-xs py-2.5 px-6">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {[["Name", user?.name], ["Email", user?.email], ["Phone", user?.phone || "Not provided"], ["Member Since", new Date().getFullYear()]].map(([l, v]) => (
                        <div key={l} className="border-b border-petal-border pb-4">
                          <p className="font-outfit text-xs uppercase tracking-widest text-petal-muted mb-1">{l}</p>
                          <p className="font-outfit text-sm text-petal-olive-text font-medium">{v}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
